import { handleAxiosAuthError } from '../utils/AxiosErrorHandling';
import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const AUTH_URL = `${BASE_API_URL}/api/v1/auth/`;

const setAuthData = ({ token, refresh_token, user_info, remember_me = false }) => {
    const storage = localStorage;
    if (token) {
        storage.setItem('access_token', token);
        if (refresh_token) storage.setItem('refresh_token', refresh_token);
    }
    if (user_info) {
        storage.setItem('user', JSON.stringify(user_info));
        storage.setItem('userId', user_info.id);
        if (user_info.phone_number) storage.setItem('phoneNumber', user_info.phone_number);
    }
};

const clearAuthData = () => {
    [localStorage, sessionStorage].forEach(storage => {
        ['access_token', 'refresh_token', 'user', 'userId', 'phoneNumber'].forEach(key => storage.removeItem(key));
    });
};

const getAuthTokens = () => {
    let token = localStorage.getItem('access_token');
    if (token) return token;
    return sessionStorage.getItem('access_token');
};

const loginUser = async (identifier, password, remember_me = false) => {
    try {
        const response = await axios.post(`${AUTH_URL}login/`, { identifier, password });
        const { access, refresh, user_id, identifier: userLabel, full_name, email, phone_number } = response.data;

        const userInfo = {
            id: user_id,
            identifier: userLabel,
            full_name: full_name || '',
            email: email || '',
            phone_number: phone_number || '',
        };

        setAuthData({
            token: access,
            refresh_token: refresh,
            user_info: userInfo,
            remember_me,
        });

        return userInfo;
    } catch (error) {
        throw handleAxiosAuthError(error, 'login');
    }
};

const registerUser = async (phone_number, email, password, full_name = '') => {
    try {
        const payload = {
            password: password,
            full_name: full_name ? String(full_name).trim() : "",
            phone_number: phone_number && phone_number.trim() !== "" ? phone_number.trim() : null,
            email: email && email.trim() !== "" ? email.trim() : null
        };
        const response = await axios.post(`${AUTH_URL}register/`, payload);
        return response.data;
    } catch (error) {
        throw handleAxiosAuthError(error, 'register');
    }
};

const requestPasswordReset = async (identifier) => {
    try {
        const response = await axios.post(`${AUTH_URL}password-reset/request/`, { identifier });
        return response.data;
    } catch (error) {
        throw handleAxiosAuthError(error, 'general');
    }
};

const confirmPasswordReset = async ({ identifier, otp_code, password }) => {
    try {
        const response = await axios.post(`${AUTH_URL}password-reset/confirm/`, {
            identifier, otp_code, password, password2: password,
        });
        return response.data;
    } catch (error) {
        throw handleAxiosAuthError(error, 'general');
    }
};

const logout = () => {
    clearAuthData();
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/';
    }
};

const authService = {
    loginUser,
    registerUser,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    getAuthTokens,
    setAuthData,
    clearAuthData,
    getUserInfo: () => {
        const user = localStorage.getItem('user') || sessionStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },
    isAuthenticated: () => !!getAuthTokens(),
};

export default authService;